import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import styled, { useTheme } from 'styled-components';
import _ from 'lodash';
import { Button } from '../../../components/Button/Button';
import { AppDispatch, RootState, useAppDispatch } from '../../../logic/store/Store';
import { getSeedsForUser } from '../../../logic/store/Seeds/SeedsActions';
import { IHybridResponse } from '../../../logic/Models/Responses/SeedResponse';
import { cornId, soyId } from '../../../logic/store/Seeds/CropsSlice';
import { startPpgPdf } from '../../../logic/Utility/registerCancelableThunks';
import { getAttributesv7 } from '../../../logic/store/Seeds/AttributesSlice';
import { Badge } from 'antd';
import { DropdownTab } from '../../../components/DropdownTab/DropdownTab';
import { ThumbnailCheckbox } from '../../../components/ThumbnailCheckbox/ThumbnailCheckbox';
import { StyledCollapse, StyledPanel } from '../../../components/StyledCollapse/StyledCollapse';
import { dynamicSort } from '../../../logic/Utility/Utils';
import { useAsyncThunkListener } from '../../../logic/Utility/useAsyncThunkListener';
import { PpgPickSeedRow } from './PpgPickSeedRow';
import PpgSelectedSeedRowList from './PpgSelectedSeedRowList';

// --- UI tab setup ---
enum MainTabs { Products = 'Products', Corn = 'Corn', Soybean = 'Soybean', Enogen = 'Enogen' }
type AttributeTab = Exclude<MainTabs, MainTabs.Products>;
type AttrUI = { Id: string; AttributeName: string; Ordinal: number; ImgSrc: string; Category: string; CropName: string };

const PpgMainContainer = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	overflow: hidden;
`;
const PpgInnerContainer = styled.div`
	height: 65%;
	width: 100%;
	display: flex;
	flex-direction: row;
`;

const mapStateToProps = (state: RootState) => ({
	AllUserSeeds: state.seeds.userSeeds,
	IsLoadingSeeds: state.seeds.isLoading,
	CropYear: state.crops.CropYear,
});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
	GetUserSeeds: (p: any) => dispatch(getSeedsForUser(p)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const PpgMainComponent: React.FC<PropsFromRedux> = ({ AllUserSeeds, IsLoadingSeeds, CropYear, GetUserSeeds }) =>
{
	const theme = useTheme();
	const dispatch = useAppDispatch();

	const [allHybridsList, setHybridsList] = useState<IHybridResponse[]>([]);
	const [selectedSeeds, setSelectedSeeds] = useState<IHybridResponse[]>([]);
	const maxSelectedSeeds = 8;

	const [selectedTab, setSelectedTab] = useState<MainTabs>(MainTabs.Products);
	const [checked, setChecked] = useState<Record<string, boolean>>({}); // `${attrId}-${tab}`: true/false
	const [uiByTab, setUiByTab] = useState<Record<AttributeTab, Record<string, AttrUI[]>>>({Corn: {}, Soybean: {}, Enogen: {}});

	// Fetch attributes once.
	useEffect(() =>
	{
		dispatch(getAttributesv7());
	}, []);

	// Fetch seeds. CropYear should never change while this modal is mounted.
	useEffect(() =>
	{
		if (CropYear) 
		{
			GetUserSeeds({ requestedYear: CropYear });
		}
	}, [CropYear]);

	// listen for v7
	const { data: v7, isLoading: isAttrLoading, isError: isAttrErr, errorMessage: attrErr } = useAsyncThunkListener(getAttributesv7);

	// seeds → local
	useEffect(() =>
	{
		if (!IsLoadingSeeds && AllUserSeeds)
		{
			const hybrids = _.cloneDeep(AllUserSeeds).flatMap(s => s.BrandApplications.flatMap(b => b.Hybrids));
			setHybridsList(hybrids);
		}
	}, [IsLoadingSeeds, AllUserSeeds]);

	// Build UI + defaults inline when v7 arrives
	useEffect(() =>
	{
		if (!v7)
		{
			return;
		}

		// Build per-crop category map for Corn/Soy categories (we only have Corn and Soy, but Enogen uses Corn's attributes)
		const makeCatMap = () => ({} as Record<string, AttrUI[]>);
		const byTab: Record<AttributeTab, Record<string, AttrUI[]>> = { Corn: makeCatMap(), Soybean: makeCatMap(), Enogen: makeCatMap() };

		const upsert = (tab: AttributeTab, cat: string, item: AttrUI) =>
		{
			const k = cat.replace(/(\b)[a-z](?!s)/g, c => c.toUpperCase()); // capitalize first letter of each word
			const tabMap = byTab[tab] || (byTab[tab] = {});
			const arr = tabMap[k] || (tabMap[k] = []);
			arr.push(item);
		};

		// 1) Build the Attribute lists (we only have Corn and Soy, but Enogen uses Corn's attributes)
		v7.AttributesByCrop.forEach(crop =>
		{
			crop.Attributes.forEach(a =>
			{
				if (a.Name.includes('Canada'))
				{
					return;
				}
				const item: AttrUI = {
					Id: a.Id, AttributeName: a.Name, Ordinal: a.Ordinal,
					ImgSrc: '/AttributeIcons/' + a.Image, Category: a.Category, CropName: crop.CropName
				};
				const cropTab = crop.CropName.toLowerCase() === 'corn' ? MainTabs.Corn : MainTabs.Soybean;
				upsert(cropTab, a.Category, item);
				// Enogen mirrors Corn visually
				if (cropTab === MainTabs.Corn)
				{
					upsert(MainTabs.Enogen, a.Category, item);
				}
			});
		});

		// 2) Seed defaults into `checked` using DefaultAttributesByCrop
		const nextChecked: Record<string, boolean> = {};
		const validIds = new Set(v7.AttributesByCrop.flatMap(c => c.Attributes.map(a => a.Id)));

		v7.DefaultAttributesByCrop.forEach(def =>
		{
			const owningCrop = v7.AttributesByCrop.find(c => c.CropId.toLowerCase() === def.CropId?.toLowerCase());
			if (!owningCrop)
			{
				return;
			}

			def.BrandApplications?.forEach(ba =>
			{
				const baName = (ba?.BrandApplication || 'Any').toLowerCase().trim();
				// Enogen gets its own tab; otherwise map to the crop's tab (Corn or Soybean)
				const tab: AttributeTab = baName === 'enogen' ? MainTabs.Enogen : owningCrop.CropName.toLowerCase() === 'corn' ? MainTabs.Corn : MainTabs.Soybean;

				ba?.SeedAttributeIds?.forEach(id =>
				{
					if (id && validIds.has(id))
					{
						nextChecked[`${id}-${tab}`] = true;
					}
				});
			});
		});

		// sort by Ordinal inside each category
		(Object.keys(byTab) as AttributeTab[]).forEach(tab =>
		{
			Object.values(byTab[tab]).forEach(list => list.sort((a,b)=>a.Ordinal-b.Ordinal));
		});

		setUiByTab(byTab);
		setChecked(nextChecked);
	}, [v7]);

	// counts for badge + limit enforcement (per current tab)
	const counts = useMemo(() =>
	{
		const out: Record<AttributeTab, number> = { Corn: 0, Soybean: 0, Enogen: 0 };
		Object.entries(checked).forEach(([k, v]) => 
		{
			if (!v)
			{
				return;
			}
			const tab = k.split('-')[5] as AttributeTab;
			if (tab in out)
			{
				out[tab] += 1;
			}
		});
		return out;
	}, [checked]);

	const isAttributeTab = (t: MainTabs): t is AttributeTab => t !== MainTabs.Products;
	const productLimitReached = isAttributeTab(selectedTab) ? counts[selectedTab] >= 24 : false;

	// seed lists
	const soyHybridList = useMemo(() => allHybridsList?.filter(h => h.CropId === soyId).sort(dynamicSort('RelativeMaturity')), [allHybridsList]);
	const soySelectedHybrids = useMemo(() => selectedSeeds?.filter(ss => ss.CropId === soyId), [selectedSeeds]);
	const cornHybridList = useMemo(() => allHybridsList?.filter(h => h.CropId === cornId).sort(dynamicSort('RelativeMaturity')), [allHybridsList]);
	const cornSelectedHybrids = useMemo(() => selectedSeeds?.filter(ss => ss.CropId === cornId), [selectedSeeds]);
	const disableSubmitButton = useMemo(() => selectedSeeds.length === 0, [selectedSeeds]);

	const selectSeed = (id: string) =>
	{
		const found = allHybridsList.find(s => s.Id === id);
		if (found) setSelectedSeeds(prev => [...prev, found]);
	};
	const deleteSelectedseed = (id: string) => setSelectedSeeds(prev => prev.filter(s => s.Id !== id));

	const onSubmit = useCallback(() => 
	{
		const HybridIds = selectedSeeds.map(s => s.Id);

		// flatten and dedupe AttributeIds
		const AttributeIds = Array.from(
			new Set(
				Object.entries(checked)
					.filter(([, v]) => v)
					.map(([k]) => k.split('-').slice(0, 5).join('-')) // strip the trailing -tab
			)
		);

		dispatch(startPpgPdf({ HybridIds, AttributeIds }));
	}, [selectedSeeds, checked]);

	return (
		<PpgMainContainer>
			<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
				<div style={{ display: 'flex', flexDirection: 'column'}}>
					<div style={{ fontWeight: theme.fontWeights.bold, fontSize: theme.fontSizes.xLarge, fontFamily: theme.fonts.heading }}>
						Create a Product Placement Guide
					</div>
					<div style={{ marginBottom: 15, fontFamily: theme.fonts.heading }}>
						Please select the products you want to include in the guide. Up to 8 products per crop.
					</div>
				</div>
				<Button
					onClick={onSubmit}
					disabled={disableSubmitButton}
					variant='main'
					height={'40px'}
					width={'125px'}
					style={{ marginLeft: 'auto' }}
				>
					Download
				</Button>
			</div>

			<div className='TabContainer' style={{ display:'flex', borderBottom:`1px solid ${theme.colors.darkGrey}`, marginBottom:12 }}>
				<DropdownTab
					selected={selectedTab === MainTabs.Products}
					containerStyle={{ width:'20vw', marginRight:10 }}
					label={MainTabs.Products}
					onClick={() => setSelectedTab(MainTabs.Products)}
				/>
				<DropdownTab
					selected={selectedTab === MainTabs.Corn}
					containerStyle={{ width:'20vw', marginRight:10 }}
					label={'Corn Attributes'}
					onClick={() => setSelectedTab(MainTabs.Corn)}
				/>
				<DropdownTab
					selected={selectedTab === MainTabs.Soybean}
					containerStyle={{ width:'20vw', marginRight:10 }}
					label={'Soybean Attributes'}
					onClick={() => setSelectedTab(MainTabs.Soybean)}
				/>
				<DropdownTab
					selected={selectedTab === MainTabs.Enogen}
					containerStyle={{ width:'20vw' }}
					label={'Enogen Attributes'}
					onClick={() => setSelectedTab(MainTabs.Enogen)}
				/>
			</div>

			{/* Seed selection area */}
			{selectedTab === MainTabs.Products && (
				<PpgInnerContainer>
					{/* Corn column */}
					<div style={{ display:'flex', flexDirection:'column', width:'50%' }}>
						<div style={{ fontWeight: theme.fontWeights.bold, fontSize: theme.fontSizes.large }}>
							{`Corn (${selectedSeeds.filter(s=>s.CropId===cornId).length} of ${maxSelectedSeeds})`}
						</div>
						<PpgPickSeedRow
							seedList={cornHybridList}
							selectSeed={selectSeed}
							selectedSeeds={cornSelectedHybrids && cornSelectedHybrids.length > 0 ? cornSelectedHybrids : []}
							disableAllSeeds={cornSelectedHybrids && cornSelectedHybrids.length === maxSelectedSeeds}
						/>
						<PpgSelectedSeedRowList
							selectedSeeds={selectedSeeds}
							deleteSelectedseed={deleteSelectedseed}
							filterId={cornId}
						/>
					</div>

					{/* Soy column */}
					<div style={{ display:'flex', flexDirection:'column', width:'50%' }}>
						<div style={{ fontWeight: theme.fontWeights.bold, fontSize: theme.fontSizes.large }}>
							{`Soybean (${selectedSeeds.filter(s=>s.CropId===soyId).length} of ${maxSelectedSeeds})`}
						</div>
						<PpgPickSeedRow
							seedList={soyHybridList}
							selectSeed={selectSeed}
							selectedSeeds={soySelectedHybrids && soySelectedHybrids.length > 0 ? soySelectedHybrids : []}
							disableAllSeeds={soySelectedHybrids && soySelectedHybrids.length === maxSelectedSeeds}
						/>
						<PpgSelectedSeedRowList
							selectedSeeds={selectedSeeds}
							deleteSelectedseed={deleteSelectedseed}
							filterId={soyId}
						/>
					</div>
				</PpgInnerContainer>
			)}

			{/* ATTRIBUTES tab content */}
			<div style={{ display: 'flex', overflowY: 'auto', flexDirection: 'column' }}>
				<div>
					{isAttrLoading ? (
						<div>Loading attributes…</div>
					) : isAttrErr ? (
						<div style={{ color: theme.colors.primary }}>{attrErr ?? 'Error loading attributes.'}</div>
					) : isAttributeTab(selectedTab) ? (
						<>
							<div style={{ display:'flex', justifyContent:'center', alignItems:'center', marginBottom:12 }}>
								<Badge
									count={`${counts[selectedTab]}/24 Attributes`}
									style={{ backgroundColor: !productLimitReached ? theme.colors.darkGrey : theme.colors.primary, fontSize: theme.fontSizes.small }}
								/>
								{productLimitReached && (
									<div style={{ position:'absolute', right:'10%', color: theme.colors.primary, fontWeight: theme.fontWeights.bold }}>
										Please remove an attribute for this crop to add another
									</div>
								)}
							</div>

							<StyledCollapse>
								{Object.entries(uiByTab[selectedTab])
									.sort(([a],[b]) => (a > b ? 1 : -1))
									.map(([category, items]) => (
										<StyledPanel key={`${selectedTab}-${category}`} header={category}>
											<div style={{ display:'flex', flexFlow:'row wrap' }}>
												{items.map(attr =>
												{
													const key = `${attr.Id}-${selectedTab}`;
													const isChecked = !!checked[key];
													return (
														<ThumbnailCheckbox
															key={key}
															checkboxId={key}
															checked={!!checked[key]}
															defaultChecked={!!checked[key]}
															disabled={productLimitReached && !checked[key]}
															label={attr.AttributeName}
															thumbnail={attr.ImgSrc}
															containerStyle={{ margin: 5 }}
															onChange={(e) => setChecked(prev => ({ ...prev, [key]: e.target.checked }))}
														/>
													);
												})}
											</div>
										</StyledPanel>
									))}
							</StyledCollapse>
						</>
					) : null}
				</div>
			</div>
		</PpgMainContainer>
	);
};

export const PpgMain = connector(PpgMainComponent);